/**
 * Where people walk: pavements recovered from the road ribbons.
 *
 * The vector worker emits roads as draped ribbons — a left and a right vertex
 * per cross-section, one run of cross-sections per road — and throws the
 * centrelines away. Rather than widen the worker protocol, this reads the
 * structure back out of the ribbon's own index buffer: `buildRoadRibbons`
 * writes each quad as `(a, a+2, a+1, a+1, a+2, a+3)`, so consecutive quads of
 * one road have `a` climbing by two, and a jump anywhere else is a new road.
 *
 * A footway, path, pedestrian street or cycleway gives one lane down its
 * middle: that is where people walk, so these lanes are weighted most. A small
 * street or service lane gives two lanes, one just outside each kerb: on
 * the pavement a town street has (OSM often tags it on the street, which the
 * tiles do not carry), or the verge of a country lane. Nobody walks on the
 * asphalt but to cross it. Busy roads give kerb lanes too, but
 * few people pick them. Motorways and railways give none.
 *
 * `annotateFootpaths` then ties them to the traffic: where a path runs
 * across a carriageway (`spans`, a mapped crossing when the path is a footway
 * or path of its own), and where one path meets another (`links`), so people
 * can turn off one onto the next, a crossing among them. The tiles carry no
 * `highway=crossing` points, but OSM maps a crossing as a short footway over
 * the street, and that is what a span on a footway is.
 */

import type { Lane } from '../traffic/lanes.js';

export interface Footpath {
  /** x, y, z triples in the chunk's local frame, on the road surface. */
  points: Float32Array;
  /** Cumulative distance to each point, metres. */
  distances: Float32Array;
  length: number;
  /** How likely people are to walk here, relative to a footway's 1. */
  weight: number;
  /**
   * For a lane along a street's kerb, which way across it is off the road:
   * +1 toward (dz, -dx) of the way it is drawn, -1 the other way. 0 down the
   * middle of a footway or path.
   */
  outward: -1 | 0 | 1;
  /** The `ROAD_STYLE` of the way it was read from: 3, a street, where the ribbon does not say. */
  style: number;
  /** Stretches of it on a carriageway, in order along it. See `annotateFootpaths`. */
  spans: RoadSpan[];
  /** Where it meets other paths of the same chunk, in order along it. */
  links: PathLink[];
}

/** A stretch of a footpath across a road's traffic lanes, metres along it. */
export interface RoadSpan {
  start: number;
  end: number;
  /**
   * A mapped crossing: a footway or path of its own running over the street,
   * where drivers must stop for someone waiting (as Swedish law has it at a
   * zebra). A kerb lane running out across a side street is not one.
   */
  zebra: boolean;
}

/** A way from one footpath onto another: at `at` along this one, onto `path` at `to` along it. */
export interface PathLink {
  at: number;
  path: number;
  to: number;
}

export interface FootpathOptions {
  /** How far out beyond a street's kerb its kerb lanes run, metres. */
  verge: number;
  /** Lanes shorter than this are not worth walking. */
  minLength: number;
  /** Ignore roads narrower than this half-width. */
  minHalfWidth: number;
}

export const DEFAULT_FOOTPATH_OPTIONS: FootpathOptions = {
  // A metre out from the kerb: on the pavement, clear of the traffic.
  verge: 1,
  minLength: 12,
  minHalfWidth: 0.3,
};

/**
 * How much people walk each `ROAD_STYLE`, and whether down the middle (a
 * way for walking) or along the kerbs (a road). Absent: nobody walks there.
 */
const WALKED: Partial<Record<number, { weight: number; middle: boolean }>> = {
  1: { weight: 0.08, middle: false },   // arterial
  2: { weight: 0.15, middle: false },   // collector
  3: { weight: 0.35, middle: false },   // residential street
  4: { weight: 0.25, middle: false },   // service lane
  5: { weight: 1.2, middle: true },     // pedestrian street
  6: { weight: 1, middle: true },       // footway, sidewalk
  7: { weight: 0.5, middle: true },     // cycleway
  8: { weight: 0.8, middle: true },     // path
  9: { weight: 0.3, middle: true },     // track
  10: { weight: 0.6, middle: true },    // steps
};

function finish(points: ArrayLike<number>, weight = 1, outward: -1 | 0 | 1 = 0, style = 3): Footpath {
  const count = points.length / 3;
  const distances = new Float32Array(count);
  for (let i = 1; i < count; i++) {
    distances[i] = distances[i - 1] + Math.hypot(
      points[i * 3] - points[(i - 1) * 3],
      points[i * 3 + 2] - points[(i - 1) * 3 + 2],
    );
  }
  return {
    points: new Float32Array(points), distances, length: distances[count - 1] ?? 0, weight,
    outward, style, spans: [], links: [],
  };
}

/** Recover both kerbside lanes of every road in a draped ribbon buffer. */
export function extractFootpaths(
  positions: Float32Array,
  indices: Uint32Array,
  options: Partial<FootpathOptions> = {},
  /** Per-vertex road data (side, distance, style, half-width), to skip styles nobody walks. */
  attributes?: Float32Array,
): Footpath[] {
  const opts = { ...DEFAULT_FOOTPATH_OPTIONS, ...options };
  const paths: Footpath[] = [];
  const quads = Math.floor(indices.length / 6);

  let runStart = -1;
  let previous = -1;
  const flush = (first: number, last: number) => {
    // Nobody strolls along a motorway or a railway line. Without the road
    // data every road is taken for a street.
    const style = attributes?.[first * 4 + 2];
    const kind = style === undefined ? 3 : Math.round(style);
    const walked = WALKED[kind];
    if (walked === undefined) return;
    // Cross-sections `first, first+2, ..., last+2`.
    const left: number[] = [];
    const right: number[] = [];
    let halfWidth = 0;
    for (let a = first; a <= last + 2; a += 2) {
      const l = a * 3;
      const r = (a + 1) * 3;
      const mx = (positions[l] + positions[r]) / 2;
      const mz = (positions[l + 2] + positions[r + 2]) / 2;
      halfWidth = Math.max(halfWidth, Math.hypot(positions[l] - positions[r], positions[l + 2] - positions[r + 2]) / 2);
      for (const [edge, out] of [[l, left], [r, right]] as const) {
        // Out past the kerb, at the kerb's own height.
        const half = Math.hypot(positions[edge] - mx, positions[edge + 2] - mz);
        const scale = half > 1e-3 ? (half + opts.verge) / half : 1;
        out.push(mx + (positions[edge] - mx) * scale, positions[edge + 1], mz + (positions[edge + 2] - mz) * scale);
      }
    }
    if (halfWidth < opts.minHalfWidth) return;
    if (walked.middle) {
      // Down the middle: the midpoint of each left and right pair.
      const middle: number[] = [];
      for (let i = 0; i < left.length; i += 3) {
        middle.push((left[i] + right[i]) / 2, (left[i + 1] + right[i + 1]) / 2, (left[i + 2] + right[i + 2]) / 2);
      }
      const path = finish(middle, walked.weight, 0, kind);
      if (path.length >= opts.minLength) paths.push(path);
      return;
    }
    // Which way is off the road: from the middle out to this kerb, against
    // (dz, -dx) of the way the road is drawn.
    const ex = right[right.length - 3] - right[0], ez = right[right.length - 1] - right[2];
    const toLeft = (left[0] - right[0]) * ez - (left[2] - right[2]) * ex;
    const sign = toLeft >= 0 ? 1 : -1;
    const kerbs = [finish(left, walked.weight, sign, kind), finish(right, walked.weight, sign === 1 ? -1 : 1, kind)];
    if (kerbs[0].length < opts.minLength) return;
    paths.push(...kerbs);
  };

  for (let q = 0; q < quads; q++) {
    const a = indices[q * 6];
    if (runStart < 0) {
      runStart = a;
    } else if (a !== previous + 2) {
      flush(runStart, previous);
      runStart = a;
    }
    previous = a;
  }
  if (runStart >= 0) flush(runStart, previous);
  return paths;
}

/** A point and direction a given distance along a path. */
export interface PathSample {
  x: number;
  y: number;
  z: number;
  /** Unit direction of travel in x,z, increasing distance. */
  dx: number;
  dz: number;
}

/**
 * How far along `path` its nearest point to (x, z) is, in the path's own
 * frame, and how far off that is, metres. For someone off their path to
 * find their way back onto it.
 */
export function nearestAlong(path: Footpath, x: number, z: number): { distance: number; off: number } {
  const p = path.points;
  let best = Infinity, along = 0;
  for (let i = 0; i + 1 < path.distances.length; i++) {
    const ax = p[i * 3], az = p[i * 3 + 2], bx = p[i * 3 + 3], bz = p[i * 3 + 5];
    const ex = bx - ax, ez = bz - az, len2 = ex * ex + ez * ez;
    const t = len2 > 1e-9 ? Math.max(0, Math.min(1, ((x - ax) * ex + (z - az) * ez) / len2)) : 0;
    const d = Math.hypot(ax + ex * t - x, az + ez * t - z);
    if (d < best) {
      best = d;
      along = path.distances[i] + (path.distances[i + 1] - path.distances[i]) * t;
    }
  }
  return { distance: along, off: best };
}

export function sampleFootpath(path: Footpath, distance: number, out: PathSample): PathSample {
  const d = Math.min(path.length, Math.max(0, distance));
  const count = path.distances.length;
  // Binary search for the segment. Paths run to a few hundred points, and
  // this runs for every villager every frame.
  let lo = 0;
  let hi = count - 1;
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1;
    if (path.distances[mid] <= d) lo = mid;
    else hi = mid;
  }
  const span = path.distances[hi] - path.distances[lo];
  const t = span > 1e-6 ? (d - path.distances[lo]) / span : 0;
  const p = path.points;
  const ax = p[lo * 3], ay = p[lo * 3 + 1], az = p[lo * 3 + 2];
  const bx = p[hi * 3], by = p[hi * 3 + 1], bz = p[hi * 3 + 2];
  out.x = ax + (bx - ax) * t;
  out.y = ay + (by - ay) * t;
  out.z = az + (bz - az) * t;
  const len = Math.hypot(bx - ax, bz - az);
  out.dx = len > 1e-6 ? (bx - ax) / len : 0;
  out.dz = len > 1e-6 ? (bz - az) / len : 1;
  return out;
}

/** A lane's segment ends, by grid cell, for finding what crosses what without trying every pair. */
class SegmentGrid {
  private readonly cells = new Map<number, number[]>();

  constructor(private readonly size: number) {}

  private key(cx: number, cz: number): number {
    return (cx + 32768) * 65536 + (cz + 32768);
  }

  /** Register item `id` over the box from (x0, z0) to (x1, z1). */
  add(id: number, x0: number, z0: number, x1: number, z1: number): void {
    for (let cx = Math.floor(Math.min(x0, x1) / this.size); cx <= Math.floor(Math.max(x0, x1) / this.size); cx++) {
      for (let cz = Math.floor(Math.min(z0, z1) / this.size); cz <= Math.floor(Math.max(z0, z1) / this.size); cz++) {
        const k = this.key(cx, cz);
        const cell = this.cells.get(k);
        if (cell === undefined) this.cells.set(k, [id]);
        else cell.push(id);
      }
    }
  }

  /** Every item registered over the box, each once. */
  query(x0: number, z0: number, x1: number, z1: number, out: Set<number>): Set<number> {
    out.clear();
    for (let cx = Math.floor(Math.min(x0, x1) / this.size); cx <= Math.floor(Math.max(x0, x1) / this.size); cx++) {
      for (let cz = Math.floor(Math.min(z0, z1) / this.size); cz <= Math.floor(Math.max(z0, z1) / this.size); cz++) {
        const cell = this.cells.get(this.key(cx, cz));
        if (cell !== undefined) for (const id of cell) out.add(id);
      }
    }
    return out;
  }
}

/** Half the width of a traffic lane, and a little more: where a car's body reaches. */
const LANE_REACH = 1.9;
/** A path meeting a lane at less than this sine of an angle runs along it, not across. */
const CROSSING_SINE = 0.4;
/** Further apart in height than this, one is on a bridge over the other. */
const LEVEL = 2.5;
/** How far past its ends a lane counts, for crossings: over a side street's mouth. */
const LANE_END_REACH = 3;
/** A path's end this close to another path joins it. */
const LINK_REACH = 3;

/**
 * Tie a chunk's footpaths to its traffic lanes and to each other, in place:
 *
 * - First, streets with a pavement or path alongside lose their kerb lanes
 *   there (`keepToPavements`), so people keep to the pavement.
 * - `spans`: every stretch of a path across a carriageway, one per road
 *   crossed (a two-way street's lanes together), from kerb to kerb as far as
 *   a car's body reaches. A footway's own spans are mapped crossings.
 * - `links`: where a path's end comes within a few metres of another path,
 *   on both of them, so someone walking either can turn onto the other.
 */
export function annotateFootpaths(paths: Footpath[], lanes: Lane[]): void {
  keepToPavements(paths);
  // Lane segments by 10 m cell: id = lane * 65536 + segment. A side
  // street's ribbon stops at the main road's kerb, short of the pavement
  // along it, so each lane's ends reach a little further, over the mouth
  // that turning cars drive through.
  const laneGrid = new SegmentGrid(10);
  const seg = new Float32Array(4);
  const laneSegment = (lane: Lane, k: number) => {
    const p = lane.points, last = lane.distances.length - 2;
    let ax = p[k * 3], az = p[k * 3 + 2], bx = p[(k + 1) * 3], bz = p[(k + 1) * 3 + 2];
    const len = Math.hypot(bx - ax, bz - az);
    if (len > 1e-6) {
      const ux = (bx - ax) / len, uz = (bz - az) / len;
      if (k === 0) { ax -= ux * LANE_END_REACH; az -= uz * LANE_END_REACH; }
      if (k === last) { bx += ux * LANE_END_REACH; bz += uz * LANE_END_REACH; }
    }
    seg[0] = ax; seg[1] = az; seg[2] = bx; seg[3] = bz;
    return seg;
  };
  lanes.forEach((lane, li) => {
    for (let k = 0; k < lane.distances.length - 1; k++) {
      const s = laneSegment(lane, k);
      laneGrid.add(li * 65536 + k, s[0], s[1], s[2], s[3]);
    }
  });
  const found = new Set<number>();
  for (const path of paths) {
    const raw: RoadSpan[] = [];
    const p = path.points;
    for (let k = 0; k < path.distances.length - 1; k++) {
      const ax = p[k * 3], az = p[k * 3 + 2], bx = p[(k + 1) * 3], bz = p[(k + 1) * 3 + 2];
      const ex = bx - ax, ez = bz - az;
      const len = Math.hypot(ex, ez);
      if (len < 1e-6) continue;
      for (const id of laneGrid.query(ax, az, bx, bz, found)) {
        const lane = lanes[Math.floor(id / 65536)], j = id % 65536, q = lane.points;
        const s = laneSegment(lane, j);
        const cx = s[0], cz = s[1], fx = s[2] - cx, fz = s[3] - cz;
        const flen = Math.hypot(fx, fz);
        if (flen < 1e-6) continue;
        const denom = ex * fz - ez * fx;
        const sine = Math.abs(denom) / (len * flen);
        if (sine < CROSSING_SINE) continue;
        // Where along each segment the two lines cross.
        const t = ((cx - ax) * fz - (cz - az) * fx) / denom;
        const u = ((cx - ax) * ez - (cz - az) * ex) / denom;
        if (t < 0 || t > 1 || u < 0 || u > 1) continue;
        const py = p[k * 3 + 1] + (p[(k + 1) * 3 + 1] - p[k * 3 + 1]) * t;
        const ly = q[j * 3 + 1] + (q[(j + 1) * 3 + 1] - q[j * 3 + 1]) * u;
        if (Math.abs(py - ly) > LEVEL) continue;
        const at = path.distances[k] + t * len;
        const reach = Math.min(6, LANE_REACH / sine);
        raw.push({ start: Math.max(0, at - reach), end: Math.min(path.length, at + reach), zebra: path.outward === 0 });
      }
    }
    raw.sort((a, b) => a.start - b.start);
    // Lanes side by side are one road to cross: merge what overlaps or nearly does.
    const spans: RoadSpan[] = [];
    for (const span of raw) {
      const last = spans[spans.length - 1];
      if (last !== undefined && span.start <= last.end + 1) last.end = Math.max(last.end, span.end);
      else spans.push({ ...span });
    }
    path.spans = spans;
    path.links = [];
  }

  // Path segments by 10 m cell: id = path * 65536 + segment.
  const pathGrid = new SegmentGrid(10);
  paths.forEach((path, pi) => {
    const p = path.points;
    for (let k = 0; k < path.distances.length - 1; k++) {
      pathGrid.add(pi * 65536 + k, p[k * 3], p[k * 3 + 2], p[(k + 1) * 3], p[(k + 1) * 3 + 2]);
    }
  });
  const seen = new Set<string>();
  const link = (from: number, at: number, to: number, there: number) => {
    const key = `${from}:${Math.round(at)}:${to}`;
    if (seen.has(key)) return;
    seen.add(key);
    paths[from].links.push({ at, path: to, to: there });
  };
  paths.forEach((path, pi) => {
    for (const end of [0, path.length]) {
      const n = end === 0 ? 0 : path.distances.length - 1;
      const x = path.points[n * 3], y = path.points[n * 3 + 1], z = path.points[n * 3 + 2];
      // The nearest point on each other path near this end.
      const best = new Map<number, { d: number; at: number }>();
      for (const id of pathGrid.query(x - LINK_REACH, z - LINK_REACH, x + LINK_REACH, z + LINK_REACH, found)) {
        const oi = Math.floor(id / 65536);
        if (oi === pi) continue;
        const other = paths[oi], k = id % 65536, q = other.points;
        const ax = q[k * 3], az = q[k * 3 + 2], ex = q[(k + 1) * 3] - ax, ez = q[(k + 1) * 3 + 2] - az;
        const len2 = ex * ex + ez * ez;
        if (len2 < 1e-8) continue;
        const t = Math.max(0, Math.min(1, ((x - ax) * ex + (z - az) * ez) / len2));
        const d = Math.hypot(ax + ex * t - x, az + ez * t - z);
        const oy = q[k * 3 + 1] + (q[(k + 1) * 3 + 1] - q[k * 3 + 1]) * t;
        if (d > LINK_REACH || Math.abs(oy - y) > LEVEL) continue;
        const at = other.distances[k] + t * Math.sqrt(len2);
        const was = best.get(oi);
        if (was === undefined || d < was.d) best.set(oi, { d, at });
      }
      for (const [oi, { at }] of best) {
        link(pi, end, oi, at);
        link(oi, at, pi, end);
      }
    }
  });
  for (const path of paths) path.links.sort((a, b) => a.at - b.at);
}

/**
 * A pavement or path this near a street's kerb lane, either side, runs
 * alongside it: across a town street from the far kerb lane, a metre out.
 */
const PAVEMENT_REACH = 10;
/** Running alongside: at no more than this to the street (the cosine of 45°). */
const ALONGSIDE = 0.7;
/** The ways that are for walking, by `ROAD_STYLE`: a pedestrian street, footway, cycleway, path, steps. */
const FOR_WALKING = new Set([5, 6, 7, 8, 10]);

/**
 * Nobody walks in the street where there is a pavement or a path beside it.
 * OSM maps a town's pavements as footways of their own (`footway=sidewalk`),
 * a few metres out from the street's line, and without this people walked
 * the gutter beside an empty pavement. Each kerb lane keeps only the
 * stretches with no way for walking within `PAVEMENT_REACH`, on either side,
 * running alongside; stretches shorter than `minLength` go too. Mutates
 * `paths`, so it runs before anything refers to them by index.
 */
export function keepToPavements(paths: Footpath[], minLength = DEFAULT_FOOTPATH_OPTIONS.minLength): void {
  const grid = new SegmentGrid(10);
  paths.forEach((path, pi) => {
    if (path.outward !== 0 || !FOR_WALKING.has(path.style)) return;
    const p = path.points;
    for (let k = 0; k < path.distances.length - 1; k++) {
      grid.add(pi * 65536 + k, p[k * 3], p[k * 3 + 2], p[(k + 1) * 3], p[(k + 1) * 3 + 2]);
    }
  });
  const found = new Set<number>();
  const kept: Footpath[] = [];
  for (const path of paths) {
    if (path.outward === 0) {
      kept.push(path);
      continue;
    }
    const p = path.points, n = path.distances.length;
    const covered = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      const a = Math.max(0, i - 1), b = Math.min(n - 1, i + 1);
      const dx = p[b * 3] - p[a * 3], dz = p[b * 3 + 2] - p[a * 3 + 2];
      const len = Math.hypot(dx, dz);
      if (len < 1e-6) continue;
      const ux = dx / len, uz = dz / len;
      const x = p[i * 3], y = p[i * 3 + 1], z = p[i * 3 + 2];
      for (const id of grid.query(x - PAVEMENT_REACH, z - PAVEMENT_REACH, x + PAVEMENT_REACH, z + PAVEMENT_REACH, found)) {
        const other = paths[Math.floor(id / 65536)], k = id % 65536, q = other.points;
        const ax = q[k * 3], az = q[k * 3 + 2], ex = q[(k + 1) * 3] - ax, ez = q[(k + 1) * 3 + 2] - az;
        const elen = Math.hypot(ex, ez);
        if (elen < 1e-6 || Math.abs((ex * ux + ez * uz) / elen) < ALONGSIDE) continue;
        const t = Math.max(0, Math.min(1, ((x - ax) * ex + (z - az) * ez) / (elen * elen)));
        if (Math.hypot(ax + ex * t - x, az + ez * t - z) > PAVEMENT_REACH) continue;
        if (Math.abs(q[k * 3 + 1] + (q[(k + 1) * 3 + 1] - q[k * 3 + 1]) * t - y) > LEVEL) continue;
        covered[i] = 1;
        break;
      }
    }
    // The stretches between: a run of uncovered points, from where cover ends to where it starts again.
    let from = -1;
    for (let i = 0; i <= n; i++) {
      const open = i < n && covered[i] === 0;
      if (open && from < 0) from = i;
      if (!open && from >= 0) {
        if (from === 0 && i === n) kept.push(path);
        else if (i - from >= 2) {
          const piece = finish(p.subarray(from * 3, i * 3), path.weight, path.outward, path.style);
          if (piece.length >= minLength) kept.push(piece);
        }
        from = -1;
      }
    }
  }
  paths.length = 0;
  paths.push(...kept);
}

/** The span of `path` that `distance` along it is on, or undefined. */
export function spanAt(path: Footpath, distance: number): RoadSpan | undefined {
  for (const span of path.spans) if (distance >= span.start && distance <= span.end) return span;
  return undefined;
}

/** The middle of every mapped crossing over a street, x, y, z in the paths' frame: where to paint a zebra. */
export function crossingPoints(paths: Footpath[]): number[] {
  const out: number[] = [];
  const at: PathSample = { x: 0, y: 0, z: 0, dx: 0, dz: 1 };
  for (const path of paths) {
    for (const span of path.spans) {
      if (!span.zebra) continue;
      sampleFootpath(path, (span.start + span.end) / 2, at);
      out.push(at.x, at.y, at.z);
    }
  }
  return out;
}
